import { isAfter } from "date-fns";
import { Control, Controller, useWatch } from "react-hook-form";

// Utils
import {
	getDateNowTimeZoneUser,
	isAfterStartFinalDate,
	isPeriodGreater,
	TypeKeysValidationsDate,
} from "@/main/services/utilsDate";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { DefaultValueParameterEnum } from "@/main/services/enum/DefaultValueParameterEnum";

// Components
import KogKeyboardDatePicker from "./keyboardDatePicker";

// Styles
import * as S from "./styles";
import KogCheckBoxControlled from "../checkbox/checkBoxControlled";

interface KogKeyboardDatePickerContainerProps {
	idScreen: string;
	nameStartDate: string;
	nameFinalDate: string;
	nameCheckbox?: string;
	labelCheckbox?: string;
	control: Control<any>;
	ignoreKeyValidations?: TypeKeysValidationsDate[];
	parameterRangeDays: number;
	isFieldInModal?: boolean;
	disabledFields?: string[];
	required?: boolean;
	alignItemDateContainer?: string;
	onChangeCheckbox?: (value: boolean) => void;
}

function KogKeyboardDatePickerContainer({
	idScreen,
	nameStartDate,
	nameFinalDate,
	nameCheckbox,
	labelCheckbox,
	control,
	isFieldInModal,
	ignoreKeyValidations = [],
	disabledFields = [],
	parameterRangeDays = DefaultValueParameterEnum.FallbackRangeDate,
	required = false,
	alignItemDateContainer = "center",
	onChangeCheckbox = () => {},
}: KogKeyboardDatePickerContainerProps) {
	const watcherStartDate = useWatch({
		control,
		name: nameStartDate,
		defaultValue: new Date(),
	});
	const watcherFinalDate = useWatch({
		control,
		name: nameFinalDate,
		defaultValue: new Date(),
	});

	const CURRENT_DATE = getDateNowTimeZoneUser().setUTCHours(23, 59, 59, 999);
	const isDateValid = (date: Date) => !isNullUndefined(date) && date instanceof Date && !isNaN(date.getTime());
	const concatIdScreen = (id: string) => `${idScreen}-txt-${id}`;

	//#region Validators
	function validateDate(typeDate: "start" | "final") {
		const OBJ_VALIDATIONS_DATE: Record<TypeKeysValidationsDate, boolean> = {
			isInvalidStartDate: typeDate === "start" && !isDateValid(watcherStartDate),
			isInvalidFinalDate: typeDate === "final" && !isDateValid(watcherFinalDate),
			finalDateGreaterThanCurrentDate: typeDate === "final" && isAfter(watcherFinalDate, CURRENT_DATE),
			rangeBetweenDays: isPeriodGreater(watcherStartDate, watcherFinalDate, parameterRangeDays),
			startDateGreaterThanFinalDate: isAfterStartFinalDate(watcherStartDate, watcherFinalDate),
		};

		const keysValidations = Object.keys(OBJ_VALIDATIONS_DATE) as TypeKeysValidationsDate[];
		const keysIgnoreValidations = keysValidations.filter((key) => !ignoreKeyValidations.includes(key));
		const keysError = keysIgnoreValidations.find((key) => OBJ_VALIDATIONS_DATE[key]);

		return !!keysError;
	}
	//#endregion

	return (
		<S.DateContainer $disabled={disabledFields.length > 1} $alignItems={alignItemDateContainer}>
			<Controller
				name={nameStartDate}
				defaultValue={new Date()}
				rules={{}}
				control={control}
				render={({ field: { name, value, ...restField }, fieldState: { error } }) => (
					<KogKeyboardDatePicker
						id={concatIdScreen(name)}
						isFieldInModal={isFieldInModal}
						name={name}
						value={value}
						required={required}
						label={getTranslateByKey("information.startDateSince")}
						error={validateDate("start") || !!error}
						disabled={disabledFields.includes(name)}
						onChange={(date) => {
							restField.onChange(date);
						}}
					/>
				)}
			/>

			<Controller
				name={nameFinalDate}
				control={control}
				render={({ field: { name, value, ...restField }, fieldState: { error } }) => (
					<KogKeyboardDatePicker
						id={concatIdScreen(name)}
						name={name}
						isFieldInModal={isFieldInModal}
						value={value}
						required={required}
						label={getTranslateByKey("information.finalDateUntil")}
						error={validateDate("final") || !!error}
						disabled={disabledFields.includes(name)}
						onChange={(date) => {
							restField.onChange(date);
						}}
					/>
				)}
			/>

			{!isNullUndefined(nameCheckbox) && (
				<S.AreaCheckbox>
					<KogCheckBoxControlled
						id={concatIdScreen("checkbox")}
						control={control}
						nameCustom={nameCheckbox}
						label={labelCheckbox ?? ""}
						onChangeCustom={onChangeCheckbox}
					/>
				</S.AreaCheckbox>
			)}
		</S.DateContainer>
	);
}

export default KogKeyboardDatePickerContainer;
